import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../static/query-keys";

import {
  createMedicalTreatmentPlanV3,
  deleteMedicalTreatmentPlanV1,
  updateMedicalTreatmentPlanV1,
} from "@/lib/data/requests/matters/medical-treatment-plan";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import toast from "react-hot-toast";

export const useCreateMedicalTreatmentPlan = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMedicalTreatmentPlanV3>[0]) =>
      createMedicalTreatmentPlanV3(data),
    onMutate: (data) => {
      const optimisticId = `optimistic-${crypto.randomUUID()}`;
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentPlans: [
                ...oldData.data.medicalTreatmentPlans,
                {
                  ...data,
                  id: optimisticId,
                  dateCreated: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
                  dateModified: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
                },
              ],
            },
          };
        },
      );

      return {
        previousData,
        optimisticId,
      };
    },
    onError: async (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      toastAPIErrorMessage(error, "Error creating treatment plan");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: (response, variables, context) => {
      queryClient.setQueryData(
        matterDetailQueryKey(variables.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return oldData;

          const nextPlans = oldData.data.medicalTreatmentPlans.map((plan) =>
            plan.id === context.optimisticId ? response.data : plan,
          );

          const hasInserted = nextPlans.some(
            (plan) => plan.id === response.data.id,
          );

          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentPlans: hasInserted
                ? nextPlans
                : [...nextPlans, response.data],
            },
          };
        },
      );
      if (!suppressSuccessToast) {
        toast.success("Treatment plan created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatmentPlan = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMedicalTreatmentPlanV1>[0]) =>
      updateMedicalTreatmentPlanV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentPlans: [
                ...oldData.data.medicalTreatmentPlans.map((plan) =>
                  plan.id === data.id ? { ...plan, ...data } : plan,
                ),
              ],
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating treatment plan");
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await invalidateSearchQueries(queryClient);
        await queryClient.invalidateQueries({
          predicate: (query) => !isCanonicalSearchQuery(query),
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Treatment plan updated successfully");
      }
    },
  });
};

export const useDeleteMedicalTreatmentPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMedicalTreatmentPlanV1>[0]) =>
      deleteMedicalTreatmentPlanV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentPlans: oldData.data.medicalTreatmentPlans.filter(
                (plan) => plan.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData,
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting treatment plan");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Treatment plan deleted successfully");
    },
  });
};
